import { useState } from 'react'
import { Link, useLocation } from 'wouter'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Icon } from '@/components/ds'
import { toast } from 'sonner'
import { useUser } from '@/contexts/UserContext'
import { ApiError } from '@/lib/api'
import { cleanRut, formatRut, validateRut } from '@/lib/rut'
import { cn } from '@/lib/utils'
import { LARGO_MINIMO, contrasenaAceptable } from '@/lib/contrasena'
import {
  AccesoCuerpo,
  AccesoLayout,
  AccesoPieTarjeta,
  AccesoTitulo,
  AvisoAcceso,
  BotonVerContrasena,
  CampoAcceso,
  EtiquetaFuerza,
  FuerzaContrasena,
  claseBotonAcceso,
} from '@/components/auth'

/** Los campos de texto del formulario, en el orden en que se leen. */
type CampoId = 'empresaRut' | 'empresaNombre' | 'nombre' | 'apellido' | 'email' | 'rutUsuario'

interface DefinicionCampo {
  id: CampoId
  label: string
  icono?: string
  placeholder: string
  /** RUT: se formatea al tipear y se valida el dígito verificador. */
  rut?: boolean
  mail?: boolean
  mono?: boolean
  /** Ocupa el ancho completo de la grilla de dos columnas. */
  completo?: boolean
  nota?: string
  autoComplete?: string
}

const CAMPOS: Record<CampoId, DefinicionCampo> = {
  empresaRut: {
    id: 'empresaRut',
    label: 'RUT de la empresa',
    icono: 'domain',
    placeholder: '76.123.456-0',
    rut: true,
    mono: true,
    autoComplete: 'off',
  },
  empresaNombre: {
    id: 'empresaNombre',
    label: 'Nombre de fantasía',
    icono: 'badge',
    placeholder: 'Cómo la conocen',
    autoComplete: 'organization',
  },
  nombre: {
    id: 'nombre',
    label: 'Nombre',
    icono: 'person',
    placeholder: 'Tu nombre',
    autoComplete: 'given-name',
  },
  apellido: {
    id: 'apellido',
    label: 'Apellido',
    placeholder: 'Tu apellido',
    autoComplete: 'family-name',
  },
  email: {
    id: 'email',
    label: 'Tu correo',
    icono: 'mail',
    placeholder: 'tu@empresa.cl',
    mail: true,
    completo: true,
    autoComplete: 'email',
  },
  rutUsuario: {
    id: 'rutUsuario',
    label: 'Tu RUT',
    icono: 'fingerprint',
    placeholder: '12.345.678-5',
    rut: true,
    mono: true,
    completo: true,
    nota: 'Con este RUT vas a iniciar sesión.',
    autoComplete: 'off',
  },
}

const ORDEN: CampoId[] = ['empresaRut', 'empresaNombre', 'nombre', 'apellido', 'email', 'rutUsuario']

const mailValido = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())

export function Registro() {
  const [, navigate] = useLocation()
  const { signup } = useUser()

  const [valores, setValores] = useState<Record<CampoId, string>>({
    empresaRut: '',
    empresaNombre: '',
    nombre: '',
    apellido: '',
    email: '',
    rutUsuario: '',
  })
  const [tocados, setTocados] = useState<Partial<Record<CampoId | 'password2', boolean>>>({})
  // Se enciende al primer intento de envío: a partir de ahí TODOS los errores
  // se muestran, incluidos los de campos que la persona nunca tocó. Antes de
  // eso, marcar en rojo un campo vacío que todavía no visitó es hostil.
  const [intentado, setIntentado] = useState(false)

  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [verPassword, setVerPassword] = useState(false)

  // Pre-marcado: en la práctica el dueño casi siempre quiere su propio correo
  // como los 3 emails de la empresa (contacto, registros, vencimientos).
  const [usarEmailEmpresa, setUsarEmailEmpresa] = useState(true)
  // Consentimiento legal: arranca SIEMPRE desmarcado. Un checkbox pre-marcado
  // no produce consentimiento válido bajo la Ley 21.719 — tiene que ser un acto
  // afirmativo de la persona. No lo cambies a `true` por comodidad de UX.
  const [aceptaTerminos, setAceptaTerminos] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null)

  const errorDe = (id: CampoId): string => {
    const campo = CAMPOS[id]
    const valor = valores[id].trim()
    if (!valor) return 'Completa este dato'
    if (campo.rut && !validateRut(valor)) return 'Ese RUT no es válido'
    if (campo.mail && !mailValido(valor)) return 'Revisa el formato del correo'
    if (id === 'empresaNombre' && valor.length < 2) return 'Muy corto'
    return ''
  }

  const setValor = (id: CampoId, bruto: string) => {
    setValores((prev) => ({ ...prev, [id]: CAMPOS[id].rut ? cleanRut(bruto) : bruto }))
    setErrorEnvio(null)
  }

  const passwordOk = contrasenaAceptable(password)
  const password2Desajustada = password2.length > 0 && password2 !== password
  const password2Ok = password2.length > 0 && !password2Desajustada

  // El contador del pie. Son 9: los 6 campos, la contraseña, su repetición y
  // la aceptación legal. Todos obligatorios — por eso la barra llega a 100%
  // exactamente cuando el botón se habilita, y no antes.
  const listos =
    ORDEN.filter((id) => !errorDe(id)).length +
    (passwordOk ? 1 : 0) +
    (password2Ok ? 1 : 0) +
    (aceptaTerminos ? 1 : 0)
  const total = ORDEN.length + 3
  const completo = listos === total
  const porcentaje = Math.round((listos / total) * 100)

  const renderCampo = (id: CampoId) => {
    const campo = CAMPOS[id]
    const error = errorDe(id)
    const mostrar = (tocados[id] || intentado) && !!error
    return (
      <div key={id} className={cn('min-w-0', campo.completo && 'sm:col-span-2')}>
        <CampoAcceso
          id={`reg-${id}`}
          label={campo.label}
          icono={campo.icono}
          denso
          mono={campo.mono}
          placeholder={campo.placeholder}
          autoComplete={campo.autoComplete}
          autoCapitalize={campo.rut ? 'characters' : undefined}
          value={campo.rut ? formatRut(valores[id]) : valores[id]}
          onChange={(v) => setValor(id, v)}
          onBlur={() => setTocados((prev) => ({ ...prev, [id]: true }))}
          listo={!error}
          error={mostrar ? error : ''}
          nota={campo.nota}
          disabled={isLoading}
        />
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return

    if (!completo) {
      setIntentado(true)
      // Sin esto, un formulario largo con un solo campo malo abajo del todo se
      // "traba" sin explicación visible en la parte de arriba de la pantalla.
      setErrorEnvio('Todavía faltan datos. Revisa lo que está marcado en rojo.')
      return
    }

    setIsLoading(true)
    setErrorEnvio(null)

    try {
      await signup({
        nombre: valores.nombre.trim(),
        apellido: valores.apellido.trim(),
        email: valores.email.trim(),
        password,
        password_confirmation: password2,
        empresa_rut: valores.empresaRut,
        // El payload sigue siendo `empresa_nombre` (contrato compartido con los
        // flujos de invitar mandante/contratista). El backend lo espeja a
        // empresa.nombre y empresa.nombre_fantasia.
        empresa_nombre: valores.empresaNombre.trim(),
        rut_usuario: valores.rutUsuario,
        usar_email_usuario: usarEmailEmpresa,
        // El backend revalida esto con `accepted` y estampa la versión vigente
        // de cada documento. Acá no se manda versión a propósito: el cliente no
        // es fuente de verdad sobre qué texto está publicado.
        acepta_terminos: aceptaTerminos,
      })
      toast.success('¡Cuenta creada correctamente!')
      navigate('/')
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422 && err.errors) {
          const primero = Object.values(err.errors)[0]?.[0]
          setErrorEnvio(primero ?? 'Revisa los datos del formulario.')
        } else {
          setErrorEnvio(err.message || 'Ocurrió un error. Intenta nuevamente.')
        }
      } else {
        setErrorEnvio('No se pudo conectar con el servidor.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AccesoLayout
      ancho="ancho"
      pie={
        <div className="flex items-center gap-[7px]">
          <span className="text-muted-foreground text-[12.5px]">¿Ya tienes cuenta?</span>
          <Link
            href="/login"
            className="text-primary-hover hover:text-primary-strong text-[12.5px] font-medium"
          >
            Inicia sesión
          </Link>
        </div>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <AccesoCuerpo className="gap-5">
          <AccesoTitulo
            titulo="Crea la cuenta de tu empresa"
            detalle="Tú quedas como administrador. Después podrás invitar al resto del equipo."
          />

          {errorEnvio && <AvisoAcceso titulo={errorEnvio} />}

          <BloqueRegistro
            icono="domain"
            titulo="Datos de la empresa"
            descripcion="Como está inscrita en el SII"
            listos={['empresaRut', 'empresaNombre'].filter((id) => !errorDe(id as CampoId)).length}
            total={2}
          >
            <div className="grid grid-cols-1 gap-[13px] sm:grid-cols-2">
              {renderCampo('empresaRut')}
              {renderCampo('empresaNombre')}
            </div>
          </BloqueRegistro>

          <BloqueRegistro
            icono="manage_accounts"
            titulo="Tu usuario de administrador"
            descripcion="Con acceso completo a la empresa"
            listos={
              ['nombre', 'apellido', 'email', 'rutUsuario'].filter(
                (id) => !errorDe(id as CampoId)
              ).length +
              (passwordOk ? 1 : 0) +
              (password2Ok ? 1 : 0)
            }
            total={6}
          >
            <div className="grid grid-cols-1 gap-[13px] sm:grid-cols-2">
              {renderCampo('nombre')}
              {renderCampo('apellido')}
              {renderCampo('email')}
              {renderCampo('rutUsuario')}
            </div>

            {/* El correo de la empresa colgado del correo personal: es una
                decisión sobre OTROS tres campos, así que va acá pegado al
                correo y no perdido en una pantalla de configuración. */}
            <label
              className={cn(
                'border-border flex cursor-pointer items-start gap-2.5 rounded-[9px] border p-3 transition-colors',
                usarEmailEmpresa ? 'bg-primary-50' : 'bg-surface-muted'
              )}
            >
              <Checkbox
                checked={usarEmailEmpresa}
                onCheckedChange={(v) => setUsarEmailEmpresa(v === true)}
                disabled={isLoading}
                className="mt-px"
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[12.5px] font-medium">
                  Usar este correo para los avisos de la empresa
                </span>
                <span className="text-meta text-[11px] leading-[1.45]">
                  {usarEmailEmpresa
                    ? 'Contacto, registros firmados y vencimientos quedan con este correo. Lo separas después en Mi Empresa.'
                    : 'Vas a definir cada correo de la empresa después, en Mi Empresa.'}
                </span>
              </span>
            </label>

            <div className="grid grid-cols-1 gap-[13px] sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-1.5">
                <CampoAcceso
                  id="reg-password"
                  label="Contraseña"
                  icono="lock"
                  denso
                  type={verPassword ? 'text' : 'password'}
                  placeholder={`Mínimo ${LARGO_MINIMO} caracteres`}
                  autoComplete="new-password"
                  value={password}
                  onChange={(v) => {
                    setPassword(v)
                    setErrorEnvio(null)
                  }}
                  disabled={isLoading}
                  accion={<EtiquetaFuerza clave={password} />}
                  sufijo={
                    <BotonVerContrasena
                      visible={verPassword}
                      onToggle={() => setVerPassword((v) => !v)}
                      denso
                    />
                  }
                />
                <FuerzaContrasena clave={password} />
              </div>

              <CampoAcceso
                id="reg-password2"
                label="Repite la contraseña"
                icono="lock"
                denso
                type={verPassword ? 'text' : 'password'}
                placeholder="Escríbela de nuevo"
                autoComplete="new-password"
                value={password2}
                onChange={(v) => {
                  setPassword2(v)
                  setErrorEnvio(null)
                }}
                onBlur={() => setTocados((prev) => ({ ...prev, password2: true }))}
                listo={password2Ok}
                error={
                  password2Desajustada && (tocados.password2 || intentado)
                    ? 'Las contraseñas no coinciden'
                    : ''
                }
                disabled={isLoading}
              />
            </div>
          </BloqueRegistro>

          {/* Consentimiento legal — separado de los bloques de datos porque no
              es un dato de la cuenta, es un acto de la persona. */}
          <label
            className={cn(
              'bg-surface-muted flex cursor-pointer items-start gap-2.5 rounded-[9px] border p-3',
              !aceptaTerminos && intentado ? 'border-destructive' : 'border-border'
            )}
          >
            <Checkbox
              checked={aceptaTerminos}
              onCheckedChange={(v) => setAceptaTerminos(v === true)}
              disabled={isLoading}
              aria-invalid={!aceptaTerminos && intentado}
              className="mt-px"
            />
            <span className="flex min-w-0 flex-col gap-[3px]">
              <span className="text-strong text-[12.5px] leading-[1.5]">
                Acepto los{' '}
                {/* <a> plano y NO el <Link> de wouter: wouter 3.x ignora
                    `target` — hace preventDefault() y navega por el router
                    igual, lo que se llevaría puesto el formulario a medio
                    llenar. Estas rutas resuelven por fallback SPA. */}
                <a
                  href="/terminos"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary-hover font-medium hover:underline"
                >
                  Términos y Condiciones
                </a>{' '}
                y la{' '}
                <a
                  href="/privacidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary-hover font-medium hover:underline"
                >
                  Política de Privacidad
                </a>
                .
              </span>
              <span className="text-meta text-[11px] leading-[1.45]">
                Incluye el tratamiento de los datos personales que registres, según la Ley N° 19.628
                y la Ley N° 21.719.
              </span>
            </span>
          </label>
        </AccesoCuerpo>

        <AccesoPieTarjeta>
          <div className="flex min-w-[150px] flex-1 flex-col gap-[5px]">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'text-[12.5px] font-medium',
                  completo ? 'text-success' : 'text-strong'
                )}
              >
                {completo ? 'Todo listo' : `${listos} de ${total} datos`}
              </span>
              <span className="text-soft text-[11.5px]">
                {completo ? 'puedes crear la cuenta' : 'todos son necesarios'}
              </span>
            </div>
            {/* Barra propia y no el `ProgressBar` del DS: ese colorea por
                umbral de cumplimiento (rojo bajo 50%), y un formulario recién
                empezado no es un incumplimiento. Acá el color sólo dice
                "avanzando" y "listo". */}
            <div
              className="bg-border-subtle h-[5px] max-w-[220px] overflow-hidden rounded-full"
              role="progressbar"
              aria-valuenow={listos}
              aria-valuemin={0}
              aria-valuemax={total}
              aria-label="Datos completados"
            >
              <div
                className={cn(
                  'h-full rounded-full transition-[width] duration-250',
                  completo
                    ? 'bg-success-bar'
                    : listos > total / 2
                      ? 'bg-primary'
                      : 'bg-border-strong'
                )}
                style={{ width: `${porcentaje}%` }}
              />
            </div>
          </div>

          <Button
            type="submit"
            size="lg"
            disabled={isLoading}
            className={cn('w-full shrink-0 sm:w-auto', claseBotonAcceso(completo))}
          >
            {isLoading ? (
              <>
                <Icon name="progress_activity" size={17} className="animate-spin" />
                Creando…
              </>
            ) : (
              <>
                Crear cuenta
                <Icon name="arrow_forward" size={17} />
              </>
            )}
          </Button>
        </AccesoPieTarjeta>
      </form>
    </AccesoLayout>
  )
}

/**
 * Un bloque del registro: encabezado con su chip de avance + los campos.
 *
 * El chip ("2 de 2" / "Listo") es lo que hace que un formulario de nueve
 * campos no se sienta como un formulario de nueve campos: la persona ve dos
 * tareas cerrables en vez de una lista larga.
 */
function BloqueRegistro({
  icono,
  titulo,
  descripcion,
  listos,
  total,
  children,
}: {
  icono: string
  titulo: string
  descripcion: string
  listos: number
  total: number
  children: React.ReactNode
}) {
  const completo = listos === total
  return (
    <section className="flex flex-col gap-[13px]">
      <header className="flex items-center gap-2.5">
        <Icon name={icono} size={18} className={completo ? 'text-primary' : 'text-soft'} />
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold tracking-[-0.01em]">{titulo}</div>
          <div className="text-meta text-[11.5px] leading-[1.4]">{descripcion}</div>
        </div>
        <span
          className={cn(
            'inline-flex h-[21px] shrink-0 items-center gap-[5px] rounded-sm px-2 text-[11px] font-medium whitespace-nowrap',
            completo ? 'bg-success-bg text-success' : 'bg-muted text-meta'
          )}
        >
          <Icon name={completo ? 'check_circle' : 'radio_button_unchecked'} size={14} />
          {completo ? 'Listo' : `${listos} de ${total}`}
        </span>
      </header>
      {children}
    </section>
  )
}
